
import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {ShoppingCart, Plus, Check} from 'lucide-react'
import { useCart, CartItem } from '../contexts/CartContext'
import { useLocation } from 'react-router-dom'

interface AddToCartButtonProps {
  product: {
    _id: string
    name: string
    nameEn?: string
    price: number
    images: string[]
    category: string
    maxQuantity?: number
  }
  specifications?: {
    roastLevel?: string
    grindSize?: string
    packaging?: string
  }
  quantity?: number
  className?: string
  size?: 'sm' | 'md' | 'lg'
  variant?: 'primary' | 'secondary' | 'minimal'
  showQuantity?: boolean
  disabled?: boolean
}

const AddToCartButton: React.FC<AddToCartButtonProps> = ({
  product,
  specifications = {},
  quantity = 1,
  className = '',
  size = 'md',
  variant = 'primary',
  showQuantity = false,
  disabled = false
}) => {
  const location = useLocation()
  const currentLang = location.pathname.startsWith('/en') ? 'en' : 'zh'
  const { addItem } = useCart()
  const [isAdding, setIsAdding] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [currentQuantity, setCurrentQuantity] = useState(quantity)

  const content = {
    zh: {
      addToCart: '加入購物車',
      adding: '加入中...',
      added: '已加入',
      quantity: '數量',
      outOfStock: '缺貨中'
    },
    en: {
      addToCart: 'Add to Cart',
      adding: 'Adding...',
      added: 'Added',
      quantity: 'Quantity',
      outOfStock: 'Out of Stock'
    }
  }

  const t = content[currentLang]

  const handleAddToCart = async () => {
    if (disabled || isAdding) return

    setIsAdding(true)

    const cartItem: Omit<CartItem, 'quantity'> & { quantity?: number } = {
      _id: product._id,
      name: product.name,
      nameEn: product.nameEn,
      price: product.price,
      image: product.images[0] || 'https://images.pexels.com/photos/894695/pexels-photo-894695.jpeg',
      category: product.category,
      specifications,
      maxQuantity: product.maxQuantity,
      quantity: currentQuantity
    }

    try {
      // 立即滾動到頁面頂部（在任何狀態更新前執行）
      window.scrollTo({ top: 0, behavior: 'smooth' })
      
      addItem(cartItem)
      
      // 顯示成功狀態
      setTimeout(() => {
        setIsAdding(false)
        setShowSuccess(true)
        
        // 重置成功狀態
        setTimeout(() => {
          setShowSuccess(false)
        }, 2000)
      }, 500)
    } catch (error) {
      console.error('Failed to add item to cart:', error)
      setIsAdding(false)
    }
  }

  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg'
  }

  const variantClasses = {
    primary: 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 hover:bg-gray-700 dark:hover:bg-gray-300',
    secondary: 'border border-gray-400 dark:border-gray-500 text-gray-800 dark:text-gray-200 hover:bg-gray-800 hover:text-white dark:hover:bg-gray-200 dark:hover:text-gray-800',
    minimal: 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800'
  }

  const baseClasses = `
    inline-flex items-center justify-center transition-all duration-200 font-light tracking-wide
    disabled:opacity-50 disabled:cursor-not-allowed
    ${sizeClasses[size]}
    ${variantClasses[variant]}
    ${className}
  `

  if (disabled) {
    return (
      <button
        disabled
        className={`${baseClasses} opacity-50 cursor-not-allowed`}
      >
        {t.outOfStock}
      </button>
    )
  }

  return (
    <div className="flex flex-col space-y-3">
      {/* Quantity Selector */}
      {showQuantity && (
        <div className="flex items-center space-x-3">
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {t.quantity}:
          </span>
          <div className="flex items-center border border-gray-300 dark:border-gray-600">
            <button
              onClick={() => setCurrentQuantity(Math.max(1, currentQuantity - 1))}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
              disabled={currentQuantity <= 1}
            >
              <Plus size={14} className="rotate-45" />
            </button>
            <span className="px-4 py-2 text-center min-w-[60px] text-gray-800 dark:text-gray-200">
              {currentQuantity}
            </span>
            <button
              onClick={() => setCurrentQuantity(Math.min(product.maxQuantity || 999, currentQuantity + 1))}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
              disabled={currentQuantity >= (product.maxQuantity || 999)}
            >
              <Plus size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Add to Cart Button */}
      <motion.button
        onClick={handleAddToCart}
        disabled={isAdding || showSuccess}
        className={baseClasses}
        whileHover={{ scale: variant === 'minimal' ? 1 : 1.02 }}
        whileTap={{ scale: variant === 'minimal' ? 1 : 0.98 }}
        animate={showSuccess ? { scale: [1, 1.05, 1] } : {}}
      >
        {isAdding ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent mr-2"></div>
            {t.adding}
          </>
        ) : showSuccess ? (
          <>
            <Check size={16} className="mr-2" />
            {t.added}
          </>
        ) : (
          <>
            <ShoppingCart size={16} className="mr-2" />
            {t.addToCart}
          </>
        )}
      </motion.button>
    </div>
  )
}

export default AddToCartButton
